import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShoppingBag, LogOut, User, LayoutDashboard, Search, ShoppingCart, Filter } from 'lucide-react';
import { toast } from 'sonner';
import FallbackImage from '@/components/FallbackImage';
import SeasonNavigation from '@/components/SeasonNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Catalog = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProducts();
    loadCart();
  }, []);

  useEffect(() => {
    // Filter products based on search, gender, and category
    let filtered = [...products];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.display_name && p.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by gender
    if (selectedGender !== 'all') {
      filtered = filtered.filter(p => {
        const gender = (p.genero || '').toLowerCase();
        if (selectedGender === 'hombre') {
          return gender.includes('hombre') || gender.includes('men') || gender === 'hombre.';
        } else if (selectedGender === 'mujer') {
          return gender.includes('mujer') || gender.includes('women') || gender === 'mujer.';
        } else if (selectedGender === 'junior') {
          return gender.includes('niño') || gender.includes('junior') || gender.includes('kid') || gender === 'niños.';
        }
        return false;
      });
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => {
        const familia = (p.familia || '').toLowerCase();
        const subfamilia = (p.subfamilia || '').toLowerCase();
        const nombre = (p.display_name || '').toLowerCase();
        const modelo = (p.model || '').toLowerCase();

        if (selectedCategory === 'botas') {
          return subfamilia.includes('bota') || nombre.includes('mid wp') || nombre.includes('high');
        } else if (selectedCategory === 'outdoor-trail') {
          return subfamilia.includes('zapatilla') || familia.includes('multideporte') || nombre.includes('trail') || nombre.includes('low');
        } else if (selectedCategory === 'barefoot') {
          // Detectar familia Barefoot o modelos específicos (Abyss, Figaro, Samoa)
          return familia.includes('barefoot') || 
                 modelo.includes('abyss') || 
                 modelo.includes('figaro') || 
                 modelo.includes('samoa');
        } else if (selectedCategory === 'sandalias') {
          return familia.includes('sandalia') || subfamilia.includes('sandalia');
        }
        return false;
      });
    }

    setFilteredProducts(filtered);
  }, [searchTerm, selectedGender, selectedCategory, products]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/products`, { headers });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const getCartItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center space-x-3">
                <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-10" />
                <span className="text-white text-xl font-bold">B2B</span>
              </Link>
              
              {/* Main Catalog Buttons */}
              <div className="flex items-center ml-8">
                <SeasonNavigation />
              </div>
            </div>

            <nav className="flex items-center space-x-4">
              {user && (
                <>
                  <Link to="/my-orders">
                    <Button data-testid="header-orders-btn" variant="ghost" className="text-white hover:text-blue-400 hover:bg-gray-800">
                      <User className="w-4 h-4 mr-2" />
                      Mis Pedidos
                    </Button>
                  </Link>
                  {(user.role === 'admin' || user.role === 'manager') && (
                    <Link to="/admin">
                      <Button data-testid="header-admin-btn" variant="ghost" className="text-white hover:text-blue-400 hover:bg-gray-800">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  <Link to="/cart">
                    <Button data-testid="header-cart-btn" variant="outline" className="relative border-white text-white hover:bg-white hover:text-black">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Carrito
                      {getCartItemCount() > 0 && (
                        <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {getCartItemCount()}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Button data-testid="header-logout-btn" onClick={onLogout} variant="outline" className="border-white text-white hover:bg-white hover:text-black">
                    <LogOut className="w-4 h-4 mr-2" />
                    Salir
                  </Button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Buscar por modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-6 text-lg border-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Approval Status Banner - Only show for non-admin users */}
        {user && user.role !== 'admin' && !user.approved && (
          <div className="max-w-4xl mx-auto mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <span className="text-2xl">⏳</span>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-bold text-yellow-900">Cuenta Pendiente de Aprobación</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  Tu cuenta está siendo revisada por nuestro equipo. Actualmente puedes ver el catálogo y precios públicos (PVPR), 
                  pero no podrás realizar pedidos ni ver precios de tarifa mayorista hasta que tu cuenta sea aprobada.
                </p>
                <p className="text-sm text-yellow-800 mt-2 font-semibold">
                  Te notificaremos por email cuando tu cuenta sea activada.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-8">
          <div className="flex items-center justify-center mb-4">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-gray-300"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
          </div>

          {showFilters && (
            <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Gender Filter */}
                <div>
                  <h3 className="font-bold text-slate-900 mb-3">Género</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'Todos' },
                      { value: 'hombre', label: 'Hombre' },
                      { value: 'mujer', label: 'Mujer' },
                      { value: 'junior', label: 'Junior' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value={option.value}
                          checked={selectedGender === option.value}
                          onChange={(e) => setSelectedGender(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Category Filter */}
                <div>
                  <h3 className="font-bold text-slate-900 mb-3">Categoría</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'Todas' },
                      { value: 'botas', label: 'Botas Outdoor' },
                      { value: 'outdoor-trail', label: 'Calzado Outdoor y Trail' },
                      { value: 'barefoot', label: 'Barefoot' },
                      { value: 'sandalias', label: 'Sandalias' }
                    ].map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="category"
                          value={option.value}
                          checked={selectedCategory === option.value}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Active Filters Summary */}
              {(selectedGender !== 'all' || selectedCategory !== 'all') && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Mostrando {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}
                    </span>
                    <Button
                      onClick={() => {
                        setSelectedGender('all');
                        setSelectedCategory('all');
                      }}
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Welcome Message */}
        {user && (
          <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h2 className="text-xl font-bold text-black mb-2">
              Bienvenido, {user.business_name}
            </h2>
            <p className="text-gray-700">
              {user.role === 'admin' 
                ? 'Panel de administrador - Precios de tarifa (PT)' 
                : (user.approved 
                    ? 'Estás viendo precios de tarifa (PT)' 
                    : 'Estás viendo precios públicos (PVPR)'
                  )
              }
            </p>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">Cargando productos...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No se encontraron productos con stock disponible</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Link 
                key={product.id} 
                to={`/product/${encodeURIComponent(product.id)}`}
                data-testid={`product-card-${product.id}`}
              >
                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  {/* Product Image */}
                  <div className="relative h-64 bg-gray-100">
                    <FallbackImage
                      images={product.images}
                      alt={product.display_name || product.model_name}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-2 right-2 bg-black text-white text-xs px-2 py-1 rounded-full">
                      Disponible
                    </span>
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-black mb-2">
                      {product.display_name || product.model_name}
                    </h3>
                    {product.sizes && product.sizes[0]?.reference && (
                      <p className="text-blue-600 text-sm mb-2 font-semibold">
                        REF: {product.sizes[0].reference}
                      </p>
                    )}
                    {product.color && (
                      <p className="text-gray-600 text-sm mb-1">
                        Color: {product.color}
                      </p>
                    )}
                    <p className="text-gray-600 text-sm mb-3">
                      {product.sizes ? product.sizes.length : 0} tallas disponibles
                    </p>
                    
                    {product.sizes && product.sizes.length > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {!user ? (
                            <p className="text-sm text-gray-500 italic" data-testid="login-to-see-prices">Inicia sesión para ver precios</p>
                          ) : (user.role === 'admin' || user.approved) && product.sizes[0].pt_price ? (
                            <>
                              <p className="text-lg font-bold text-blue-600">
                                €{product.sizes[0].pt_price.toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-500">Precio tarifa</p>
                              <p className="text-sm text-gray-400 line-through">
                                €{product.sizes[0].pvpr_price.toFixed(2)} PVPR
                              </p>
                            </>
                          ) : (
                            <>
                              {product.sizes[0].pvpr_price > 0 ? (
                                <>
                                  <p className="text-lg font-bold text-black">
                                    €{product.sizes[0].pvpr_price.toFixed(2)}
                                  </p>
                                  <p className="text-xs text-gray-500">PVPR</p>
                                </>
                              ) : (
                                <p className="text-sm text-gray-500 italic">Consultar precio</p>
                              )}
                            </>
                          )}
                        </div>
                        <Button data-testid={`view-product-btn-${product.id}`} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                          Ver Detalles
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Catalog;

// Build refresh: 1776752355
